/**
 * useStreamingUsers -- React hook for fetching all upstream media server users.
 *
 * Endpoint:
 * - GET /api/streaming/users
 *
 * Returns the merged user list across all configured servers (Emby/Jellyfin).
 */
import { useCallback, useEffect, useRef, useState } from "react";

// ============================================================================
// Types (mirrored from backend UpstreamUserInfo + serverType)
// ============================================================================

export interface UpstreamUserPolicy {
  IsAdministrator?: boolean;
  IsDisabled?: boolean;
  IsHidden?: boolean;
  EnableRemoteAccess?: boolean;
  SimultaneousStreamLimit?: number;
  RemoteClientBitrateLimit?: number;
  MaxParentalRating?: number;
  EnableContentDeletion?: boolean;
  EnableContentDownloading?: boolean;
}

/** WSD-local analytics summary (from ?enrich=true) */
export interface UserWsdSummary {
  totalWatchTimeSecs: number;
  sessionsCount: number;
  uniqueDevices: number;
  lastWatchTitle: string | null;
  lastWatchType: string | null;
  lastWatchAt: number | null;
  lastIp: string | null;
  lastGeoCity: string | null;
  lastGeoCountry: string | null;
}

export interface UpstreamUser {
  Id: string;
  Name: string;
  ServerId?: string;
  HasPassword?: boolean;
  HasConfiguredPassword?: boolean;
  LastLoginDate?: string | null;
  LastActivityDate?: string | null;
  PrimaryImageTag?: string | null;
  Policy?: UpstreamUserPolicy;
  serverType: "emby" | "jellyfin";
  /** Enriched WSD-local analytics (present when fetched with enrich=true) */
  wsdSummary?: UserWsdSummary;
}

// ============================================================================
// Cache
// ============================================================================

interface CachedUsers {
  users: UpstreamUser[];
  fetchedAt: number;
}

const usersCache = new Map<string, CachedUsers>();
const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes
const CACHE_KEY = "streaming-users";

// ============================================================================
// useStreamingUsers
// ============================================================================

export function useStreamingUsers(enabled: boolean = false): {
  users: UpstreamUser[] | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [users, setUsers] = useState<UpstreamUser[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fetchedKeyRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  const fetchUsers = useCallback(
    async (force?: boolean) => {
      if (!enabled) return;

      // Check cache (unless forced)
      if (!force) {
        const cached = usersCache.get(CACHE_KEY);
        if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
          setUsers(cached.users);
          setError(null);
          fetchedKeyRef.current = CACHE_KEY;
          return;
        }
      }

      // Skip if already fetching
      if (isFetchingRef.current && fetchedKeyRef.current === CACHE_KEY) return;

      // Abort any in-flight request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);

      try {
        const res = await fetch("/api/streaming/users?enrich=true", { signal: controller.signal });
        const data = await res.json();

        if (!res.ok || !data.ok || !data.data?.users) {
          throw new Error(data.message ?? "Failed to load streaming users");
        }

        const fetchedUsers = data.data.users as UpstreamUser[];
        setUsers(fetchedUsers);
        setError(null);

        // Cache
        usersCache.set(CACHE_KEY, {
          users: fetchedUsers,
          fetchedAt: Date.now(),
        });
        fetchedKeyRef.current = CACHE_KEY;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        const message = err instanceof Error ? err.message : "Failed to load streaming users";
        setError(message);
      } finally {
        isFetchingRef.current = false;
        setIsLoading(false);
      }
    },
    [enabled]
  );

  // Fetch on mount or when enabled changes
  useEffect(() => {
    if (!enabled) {
      setUsers(null);
      setError(null);
      fetchedKeyRef.current = null;
      return;
    }

    if (fetchedKeyRef.current !== CACHE_KEY) {
      void fetchUsers();
    }

    return () => {
      abortRef.current?.abort();
    };
  }, [enabled, fetchUsers]);

  const refetch = useCallback(() => {
    void fetchUsers(true);
  }, [fetchUsers]);

  return { users, isLoading, error, refetch };
}
